import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useState, useContext } from "react";
import { trpc } from "../../trpc";
import MerchantContext from "../../contexts/MerchantContext";

type InventorySystemType = "simi" | "mobilia" | "domus" | "softim" | "smarthome" | "wasi";

type InventoryConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
  systemType: InventorySystemType;
  systemName: string;
  existingConfig?: any;
};

const InventoryConfigModal = ({
  isOpen,
  onClose,
  systemType,
  systemName,
  existingConfig,
}: InventoryConfigModalProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);

  // Simi fields (API key not prefilled for security)
  const [simiApiKey, setSimiApiKey] = useState("");
  const [simiMerchantId, setSimiMerchantId] = useState(existingConfig?.merchantId || "");
  const [simiUseInternalCode, setSimiUseInternalCode] = useState(
    existingConfig?.useInternalCode || false
  );

  // Mobilia fields (URL not prefilled for security)
  const [mobiliaUrl, setMobiliaUrl] = useState("");

  // Domus fields (API key not prefilled for security)
  const [domusApiKey, setDomusApiKey] = useState("");
  const [domusUseInternalCode, setDomusUseInternalCode] = useState(
    existingConfig?.useInternalCode || false
  );

  // Softim fields (API key not prefilled for security)
  const [softimApiKey, setSoftimApiKey] = useState("");
  const [softimMerchantId, setSoftimMerchantId] = useState(existingConfig?.merchantId || "");

  // SmartHome fields (company code not prefilled for security)
  const [smartHomeCompanyCode, setSmartHomeCompanyCode] = useState("");
  const [smartHomeProjectCode, setSmartHomeProjectCode] = useState(
    existingConfig?.projectCode || ""
  );

  // WASI fields (token not prefilled for security)
  const [wasiToken, setWasiToken] = useState("");
  const [wasiCompanyId, setWasiCompanyId] = useState("");
  const [wasiBuildListingUrl, setWasiBuildListingUrl] = useState(
    existingConfig?.buildListingUrl ?? false
  );
  const [wasiFincaraizProfileUrl, setWasiFincaraizProfileUrl] = useState(
    existingConfig?.fincaraizProfileUrl ?? ""
  );
  const [wasiMetrocuadradoProfileUrl, setWasiMetrocuadradoProfileUrl] = useState(
    existingConfig?.metrocuadradoProfileUrl ?? ""
  );

  const utils = trpc.useUtils();
  const updateInventoryConfig = trpc.merchants.updateInventoryConfig.useMutation({
    onSuccess: async () => {
      await utils.merchants.get.invalidate({ id: merchantId });
      toast.success("Configuración guardada", {
        description: `${systemName} ha sido configurado correctamente`,
      });
      onClose();
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "No se pudo guardar la configuración",
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!merchantId) return;

    let config: any = { type: systemType };

    switch (systemType) {
      case "simi":
        config = {
          type: "simi",
          apiKey: simiApiKey,
          merchantId: simiMerchantId || undefined,
          useInternalCode: simiUseInternalCode,
        };
        break;
      case "mobilia":
        config = {
          type: "mobilia",
          url: mobiliaUrl,
        };
        break;
      case "domus":
        config = {
          type: "domus",
          apiKey: domusApiKey,
          useInternalCode: domusUseInternalCode,
        };
        break;
      case "softim":
        config = {
          type: "softim",
          apiKey: softimApiKey,
          merchantId: softimMerchantId,
        };
        break;
      case "smarthome":
        config = {
          type: "smarthome",
          companyCode: smartHomeCompanyCode,
          projectCode: smartHomeProjectCode,
        };
        break;
      case "wasi":
        config = {
          type: "wasi",
          token: wasiToken || undefined,
          companyId: wasiCompanyId || undefined,
          buildListingUrl: wasiBuildListingUrl,
          fincaraizProfileUrl: wasiFincaraizProfileUrl || undefined,
          metrocuadradoProfileUrl: wasiMetrocuadradoProfileUrl || undefined,
        };
        break;
    }

    await updateInventoryConfig.mutateAsync({
      merchantId,
      crmType: systemType,
      config,
    });
  };

  const renderFields = () => {
    switch (systemType) {
      case "simi":
        return (
          <>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                API Key {!existingConfig?.isConfigured && <span className="text-red-500">*</span>}
                {existingConfig?.isConfigured && (
                  <Badge className="ml-2 bg-green-100 text-green-800 text-sm">Configurado</Badge>
                )}
              </Label>
              <Input
                type="password"
                value={simiApiKey}
                onChange={(e) => setSimiApiKey(e.target.value)}
                placeholder={
                  existingConfig?.isConfigured ? "••••••••••••••••" : "Ingresa tu API Key de Simi"
                }
              />
              {existingConfig?.isConfigured && (
                <p className="text-xs text-gray-600">
                  Deja en blanco para mantener el valor actual, o ingresa uno nuevo para actualizar
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">Merchant ID (Opcional)</Label>
              <Input
                value={simiMerchantId}
                onChange={(e) => setSimiMerchantId(e.target.value)}
                placeholder="ID del comercio en Simi"
              />
              <p className="text-xs text-gray-600">
                Solo el nombre, ej: "tuempresa" para tuempresa.mobilia-inventario.com
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={simiUseInternalCode}
                onCheckedChange={(checked) => setSimiUseInternalCode(checked as boolean)}
              />
              <label className="text-sm">Usar código interno</label>
            </div>
          </>
        );

      case "mobilia":
        return (
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">
              URL de Endpoint{" "}
              {!existingConfig?.isConfigured && <span className="text-red-500">*</span>}
              {existingConfig?.isConfigured && (
                <Badge className="ml-2 bg-green-100 text-green-800 text-sm">Configurado</Badge>
              )}
            </Label>
            <Input
              value={mobiliaUrl}
              onChange={(e) => setMobiliaUrl(e.target.value)}
              placeholder={
                existingConfig?.isConfigured
                  ? "••••••••••••••••"
                  : "https://api.mobilia-inventario.com/v1"
              }
            />
            <p className="text-xs text-gray-600">
              {existingConfig?.isConfigured
                ? "Deja en blanco para mantener el valor actual, o ingresa uno nuevo para actualizar"
                : "Ingresa la URL completa del endpoint de Mobilia"}
            </p>
          </div>
        );

      case "domus":
        return (
          <>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                API Key {!existingConfig?.isConfigured && <span className="text-red-500">*</span>}
                {existingConfig?.isConfigured && (
                  <Badge className="ml-2 bg-green-100 text-green-800 text-sm">Configurado ✓</Badge>
                )}
              </Label>
              <Input
                type="password"
                value={domusApiKey}
                onChange={(e) => setDomusApiKey(e.target.value)}
                placeholder={
                  existingConfig?.isConfigured ? "••••••••••••••••" : "Ingresa tu API Key de Domus"
                }
              />
              {existingConfig?.isConfigured && (
                <p className="text-xs text-gray-600">
                  Deja en blanco para mantener el valor actual, o ingresa uno nuevo para actualizar
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={domusUseInternalCode}
                onCheckedChange={(checked) => setDomusUseInternalCode(checked as boolean)}
              />
              <label className="text-sm">Usar código interno</label>
            </div>
          </>
        );

      case "softim":
        return (
          <>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                API Key {!existingConfig?.isConfigured && <span className="text-red-500">*</span>}
                {existingConfig?.isConfigured && (
                  <Badge className="ml-2 bg-green-100 text-green-800 text-sm">Configurado</Badge>
                )}
              </Label>
              <Input
                type="password"
                value={softimApiKey}
                onChange={(e) => setSoftimApiKey(e.target.value)}
                placeholder={
                  existingConfig?.isConfigured ? "••••••••••••••••" : "Ingresa tu API Key de Softim"
                }
              />
              {existingConfig?.isConfigured && (
                <p className="text-xs text-gray-600">
                  Deja en blanco para mantener el valor actual, o ingresa uno nuevo para actualizar
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Merchant ID <span className="text-red-500">*</span>
              </Label>
              <Input
                value={softimMerchantId}
                onChange={(e) => setSoftimMerchantId(e.target.value)}
                placeholder="ID del comercio en Softim"
              />
            </div>
          </>
        );

      case "smarthome":
        return (
          <>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Código de Empresa{" "}
                {!existingConfig?.isConfigured && <span className="text-red-500">*</span>}
                {existingConfig?.isConfigured && (
                  <Badge className="ml-2 bg-green-100 text-green-800 text-sm">Configurado</Badge>
                )}
              </Label>
              <Input
                value={smartHomeCompanyCode}
                onChange={(e) => setSmartHomeCompanyCode(e.target.value)}
                placeholder={
                  existingConfig?.isConfigured ? "••••••••••••••••" : "Código de empresa"
                }
              />
              {existingConfig?.isConfigured && (
                <p className="text-xs text-gray-600">
                  Deja en blanco para mantener el valor actual, o ingresa uno nuevo para actualizar
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Código de Proyecto <span className="text-red-500">*</span>
              </Label>
              <Input
                value={smartHomeProjectCode}
                onChange={(e) => setSmartHomeProjectCode(e.target.value)}
                placeholder="Código de proyecto"
              />
            </div>
          </>
        );

      case "wasi":
        return (
          <>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Token {!existingConfig?.isConfigured && <span className="text-red-500">*</span>}
                {existingConfig?.isConfigured && (
                  <Badge className="ml-2 bg-green-100 text-green-800 text-sm">Configurado</Badge>
                )}
              </Label>
              <Input
                type="password"
                value={wasiToken}
                onChange={(e) => setWasiToken(e.target.value)}
                placeholder={existingConfig?.isConfigured ? "••••••••••••••••" : "Token de WASI"}
              />
              {existingConfig?.isConfigured && (
                <p className="text-xs text-gray-600">
                  Deja en blanco para mantener el valor actual, o ingresa uno nuevo para actualizar
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Company ID{" "}
                {!existingConfig?.isConfigured && <span className="text-red-500">*</span>}
              </Label>
              <Input
                value={wasiCompanyId}
                onChange={(e) => setWasiCompanyId(e.target.value)}
                placeholder={
                  existingConfig?.isConfigured ? "••••••••••••••••" : "ID de la empresa en WASI"
                }
              />
              {existingConfig?.isConfigured && (
                <p className="text-xs text-gray-600">
                  Deja en blanco para mantener el valor actual, o ingresa uno nuevo para actualizar
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={wasiBuildListingUrl}
                onCheckedChange={(checked) => setWasiBuildListingUrl(checked === true)}
              />
              <label className="text-sm">
                Construir URL del inmueble (usar formato de URL en vez del link de WASI)
              </label>
            </div>
            <div className="space-y-2">
              <Label>URL perfil Finca Raíz</Label>
              <Input
                placeholder="https://www.fincaraiz.com.co/inmobiliarias/slug/id"
                value={wasiFincaraizProfileUrl}
                onChange={(e) => setWasiFincaraizProfileUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                URL del perfil de la inmobiliaria en Finca Raíz
              </p>
            </div>
            <div className="space-y-2">
              <Label>URL perfil Metro Cuadrado</Label>
              <Input
                placeholder="https://www.metrocuadrado.com/inmobiliaria/slug/id"
                value={wasiMetrocuadradoProfileUrl}
                onChange={(e) => setWasiMetrocuadradoProfileUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                URL del perfil de la inmobiliaria en Metro Cuadrado
              </p>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="h-full max-h-full md:h-auto md:max-h-[85vh] md:max-w-lg">
        <DialogHeader>
          <DialogTitle>Configurar {systemName}</DialogTitle>
        </DialogHeader>
        <div className="pb-6">
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-600">
                Configura las credenciales para conectar con tu ERP
              </p>

              {renderFields()}

              <Button
                type="submit"
                className="mt-4 w-full"
                disabled={updateInventoryConfig.isLoading}
              >
                {updateInventoryConfig.isLoading ? "Guardando..." : "Guardar Configuración"}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InventoryConfigModal;
